import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChartNoAxesColumn, Clock3, ListChecks, ReceiptText, Search, Settings2, UserRound, UsersRound, X } from "lucide-react";
import type { BootstrapResponse } from "../types";
import { AccountDialog } from "./AccountDialog";
import { GeneralSettings } from "./GeneralSettings";
import { PlannerRulesWorkspace } from "./PlannerRulesWorkspace";
import { StudioAdmin } from "./StudioAdmin";
import { SettlementConsoleBody } from "./SettlementConsole";
import { PayrollWorkspace } from "./SettlementManagement";
import { CommuteHistory } from "./CommuteHistory";
import { periodRange } from "./PeriodFilter";
import { today } from "../payrollPresentation";
import "./SettingsDialog.css";

/* 설정 창 = Claude 설정 창 (2026-10-18, 로그인된 claude.ai 에서 getComputedStyle 실측).
   프로필을 누르면 뜬다. 판 위에 흩어져 있던 관리 손잡이(작업자 관리 · 성과)와 계정 창을 한
   창으로 모은다 — 들어가는 문이 하나다. 왼쪽 목록에서 칸을 고르면 오른쪽에 그 칸이 선다. */

type SectionId = "general" | "account" | "workers" | "performance" | "settlement" | "planner-rules" | "commute";

export function SettingsDialog({ username, name, role, isAdmin, canEdit, canViewPlannerRules = false, canEditPlannerRules = false, data, refresh, theme, onThemeChange, onPasswordChanged, onLogout, onClose, onActivityHistory, initial = "general" }: {
  username: string; name: string; role: string; isAdmin: boolean; canEdit: boolean;
  canViewPlannerRules?: boolean; canEditPlannerRules?: boolean;
  data?: BootstrapResponse; refresh?: () => Promise<void>;
  theme: "system" | "light" | "dark"; onThemeChange: (next: "system" | "light" | "dark") => void;
  onPasswordChanged: () => void; onLogout: () => Promise<boolean>; onClose: () => void;
  onActivityHistory?: () => void;
  initial?: SectionId;
}) {
  const [query, setQuery] = useState("");
  const [rulesBusy, setRulesBusy] = useState(false);
  /* 정산 칸의 예정 / 내역 — Connectors 의 Yours / Discover 자리(제목 바로 옆 세그먼트). */
  const [payStage, setPayStage] = useState<"due" | "done">("due");
  /* 성과 칸의 기간 — 그림 이름 줄 오른쪽 세그먼트. 점 하나는 늘 하루라 집계 단위는 두지 않는다(2026-10-19 밤 지윤). 기본 6개월. */
  const [perfDays, setPerfDays] = useState<30 | 91 | 183 | 365>(183);
  const sections = useMemo(() => {
    const list: Array<{ group: "설정"; id: SectionId; label: string; icon: ReactNode; ready: boolean }> = [
      /* 2026-10-19 지윤 지시: 저쪽 설정 창처럼 **일반**이 첫 칸이고 테마가 거기 산다. */
      { group: "설정", id: "general", label: "일반", icon: <Settings2 size={20} aria-hidden="true" />, ready: true },
      { group: "설정", id: "planner-rules", label: "운영 규칙", icon: <ListChecks size={20} aria-hidden="true" />, ready: false /* 이번 배포에서는 사용자에게 안 보이게 숨긴다(2026-11-02 지윤). 되살리려면 canViewPlannerRules 로 */ },
      { group: "설정", id: "account", label: "계정", icon: <UserRound size={20} aria-hidden="true" />, ready: true },
      { group: "설정", id: "workers", label: "작업자", icon: <UsersRound size={20} aria-hidden="true" />, ready: canEdit && !!data && !!refresh },
      { group: "설정", id: "commute", label: "출퇴근", icon: <Clock3 size={20} aria-hidden="true" />, ready: canEdit },
      { group: "설정", id: "settlement", label: "정산", icon: <ReceiptText size={20} aria-hidden="true" />, ready: canEdit && !!data },
      { group: "설정", id: "performance", label: "성과", icon: <ChartNoAxesColumn size={20} aria-hidden="true" />, ready: canEdit && !!data },
    ];
    return list.filter(item => item.ready);
  }, [canEdit, data, refresh, isAdmin, canViewPlannerRules]);
  const [active, setActive] = useState<SectionId>(sections.some(item => item.id === initial) ? initial : "general");
  /* 정산의 기본 보기는 「예정」이다 — 내역을 보다 다른 칸에 갔다 오면 예정으로 다시 연다(2026-10-19 밤 지윤). */
  useEffect(() => { if (active !== "settlement") setPayStage("due"); }, [active]);
  const shown = sections.filter(item => !query.trim() || item.label.includes(query.trim()));

  /* 설정 창이 떠 있는 동안 그 안에서 여는 창(작업자 등록·공지·수정)은 새 창이 아니라 오른쪽 내용 칸에
     한 단계 들어간 화면으로 앉는다 — 표시는 html 의 클래스 하나로 CSS 가 맡는다(SettingsDialog.css). */
  useEffect(() => {
    document.documentElement.classList.add("settings-open");
    return () => document.documentElement.classList.remove("settings-open");
  }, []);
  useEffect(() => {
    const escape = (event: KeyboardEvent) => { if (!rulesBusy && event.key === "Escape" && !document.querySelector("dialog[open]")) onClose(); };
    window.addEventListener("keydown", escape);
    return () => window.removeEventListener("keydown", escape);
  }, [onClose, rulesBusy]);

  const title = sections.find(item => item.id === active)?.label ?? "";
  return <div className="settings-layer" onMouseDown={event => { if (!rulesBusy && event.target === event.currentTarget) onClose(); }}>
    <div className="settings-dialog" role="dialog" aria-modal="true" aria-label="설정">
      {/* 좁은 화면(768 미만)에서만 보이는 큰 제목 — claude.ai 설정의 "Settings" 자리. 그때 왼쪽 목록은 이 아래 글자 탭 줄이 된다. */}
      <h1 className="settings-narrow-title" aria-hidden="true">설정</h1>
      <nav className="settings-nav" aria-label="설정 목록">
        <label className="settings-search">
          <Search size={18} aria-hidden="true" />
          <input value={query} onChange={event => setQuery(event.target.value)} placeholder="검색" aria-label="설정 검색" />
        </label>
        {(["설정"] as const).map(group => {
          const items = shown.filter(item => item.group === group);
          if (!items.length) return null;
          return <div className="settings-nav-group" key={group}>
            <span className="settings-nav-label">{group}</span>
            <ul>
              {items.map(item => <li key={item.id}>
                <button type="button" className="settings-nav-row" aria-current={active === item.id ? "page" : undefined}
                  disabled={rulesBusy}
                  onClick={() => setActive(item.id)}>{item.icon}<span>{item.label}</span></button>
              </li>)}
            </ul>
          </div>;
        })}
      </nav>
      <section className={`settings-content settings-content--${active}`} aria-label={title}>
        <header className="settings-content-head">
          <h2>{title}</h2>
          {active === "settlement" && <div className="reflect-seg settings-title-seg" role="radiogroup" aria-label="정산 상태">
            {([["due", "예정"], ["done", "내역"]] as const).map(([value, label]) =>
              <button key={value} type="button" role="radio" aria-checked={payStage === value} onClick={() => setPayStage(value)}>{label}</button>)}
          </div>}

          <button type="button" className="settings-close" aria-label="닫기" title="닫기" disabled={rulesBusy} onClick={onClose}><X size={20} aria-hidden="true" /></button>
        </header>
        <div className="settings-content-body">
          {active === "planner-rules" && canViewPlannerRules && <PlannerRulesWorkspace canEdit={canEditPlannerRules} isAdmin={isAdmin} onBusyChange={setRulesBusy} />}
          {active === "general" && <GeneralSettings theme={theme} onThemeChange={onThemeChange} data={data} refresh={refresh} actorName={name} canEdit={canEdit} />}
          {active === "account" && <AccountDialog embedded username={username} name={name} role={role} isAdmin={isAdmin}
            theme={theme} onThemeChange={onThemeChange} onPasswordChanged={onPasswordChanged} onClose={onClose} onLogout={onLogout} onSettings={() => undefined} onActivityHistory={onActivityHistory} />}
          {active === "workers" && data && refresh && <StudioAdmin data={data} refresh={refresh} actorName={name} canEdit={canEdit} onExit={onClose} />}
          {active === "commute" && canEdit && <CommuteHistory />}
          {active === "performance" && data && <SettlementConsoleBody data={data} actorName={name} mode="performance" scale="day" periodDays={perfDays}
            periodName={({ 30: "1개월", 91: "3개월", 183: "6개월", 365: "1년" } as const)[perfDays]}
            scaleControl={<div className="reflect-seg performance-scale-seg" role="radiogroup" aria-label="기간">
            {([[30, "1개월"], [91, "3개월"], [183, "6개월"], [365, "1년"]] as const).map(([value, label]) =>
              <button key={value} type="button" role="radio" aria-checked={perfDays === value} onClick={() => setPerfDays(value)}>{label}</button>)}
          </div>} />}
          {active === "settlement" && data && <div className="settlement-console">
            <PayrollWorkspace data={data} actorName={name} range={periodRange({ type: "all" }, today())} stage={payStage} onStage={setPayStage} />
          </div>}
        </div>
      </section>
    </div>
  </div>;
}
